import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdAccessTime, MdDateRange, MdDelete, MdEdit } from 'react-icons/md';
import { IconButton } from '../../../components/IconButton';
import ReactIcon from '../../../components/ReactIcon';
import { DateUtils } from '../../../helpers/dateUtils';
import {
    CartServiceType,
    CartValuesServiceStore,
    useShoppingCartStore,
} from '../../../store/shoppingCart/shoppingCartStore';
import { CheckoutSubItemType } from '../types';
import { CartSubItemContainer } from './styles';
import inputMasker from '../../../helpers/inputMasker';
import { DependentDTO } from '../../../DTO/dependentsResponseDTO';
import { SelectDependentsNameInput } from './InputSelectDependetsName';
import { Format } from '../../../helpers/formatters';
import {
    FieldError,
    FieldErrorsImpl,
    Merge,
    UseFormRegister,
    UseFormSetValue,
} from 'react-hook-form';
import { Checkbox } from '../../../components/Checkbox';
import { isUndecidedDate } from '../../../components/Calendar';

type errorsType =
    | Merge<
          FieldError,
          (
              | Merge<
                    FieldError,
                    FieldErrorsImpl<{
                        name: string;
                        document: string;
                        foreigner: boolean;
                    }>
                >
              | undefined
          )[]
      >
    | undefined;

type registerType = UseFormRegister<{
    products: {
        name: string;
        document: string;
        foreigner: boolean;
    }[];
}>;

type setValueType = UseFormSetValue<{
    products: {
        name: string;
        document: string;
        foreigner: boolean;
    }[];
}>;

type DefaultProps = {
    subItem: CheckoutSubItemType;
    handleEditTime: () => void;
    isInsideCombo?: boolean;
    itemType: CartServiceType;
    isLimitedChoices: boolean;
    dependents: DependentDTO[];
    register: registerType;
    error?: errorsType;
    index: number;
    setValue: setValueType;
    shouldBlockEditing?: boolean;
    shouldBlockItemChanges?: boolean;
    isPaymentLink?: boolean;
    isForeignerInitialValue?: boolean;
    isMissingSelection?: boolean;
};

type CondicionalProps =
    | { isInsideCombo: true; handleRemoveItem?: undefined }
    | { isInsideCombo: false; handleRemoveItem: () => void };

export const CartSubItem = ({
    subItem,
    handleEditTime,
    handleRemoveItem,
    isInsideCombo,
    isLimitedChoices,
    dependents,
    shouldBlockEditing,
    shouldBlockItemChanges,
    isPaymentLink,
    register,
    error,
    index,
    setValue,
    isForeignerInitialValue,
    isMissingSelection,
}: DefaultProps & CondicionalProps) => {
    const [isForeigner, setIsForeigner] = useState<boolean>(
        isForeignerInitialValue || false
    );
    const isItemEditingBlocked = Boolean(shouldBlockItemChanges);
    const { cartValues, editCartValue } = useShoppingCartStore();

    const findSubItem = useMemo(
        () => cartValues?.find(item => item.idCart === subItem.id),
        [cartValues, subItem.id]
    );

    function handleChangeIsForeigner() {
        setValue(`products.${index}.foreigner`, !isForeigner);
        handleChangeCartValue(findSubItem?.client?.document || '', 'foreigner');
        setIsForeigner(!isForeigner);
    }

    const handleChangeCartValue = useCallback(
        (target: string, type: 'name' | 'document' | 'foreigner') => {
            if (!findSubItem) return;
            const newValueCart: CartValuesServiceStore = {
                ...findSubItem,
                client: {
                    name:
                        type === 'name'
                            ? target
                            : findSubItem.client?.name || '',
                    document:
                        type === 'document'
                            ? !isForeigner
                                ? inputMasker.formatValue(target, 'CPF')
                                : target
                            : type === 'foreigner'
                              ? target
                              : findSubItem.client?.document || '',
                    foreigner:
                        type === 'foreigner'
                            ? !isForeigner
                            : findSubItem.client?.foreigner || false,
                },
            };

            editCartValue(findSubItem.idCart!, newValueCart, true);
        },
        [editCartValue, findSubItem, isForeigner]
    );

    const handleDependentDropdownChange = (name: string) => {
        const targetDependent = dependents.find(d => d.name === name);

        if (!targetDependent) return;
        if (!findSubItem) return;

        const newValueCart: CartValuesServiceStore = {
            ...findSubItem,
            client: {
                document: targetDependent.document,
                name: targetDependent.name,
                foreigner: targetDependent.foreigner,
            },
        };

        
        editCartValue(findSubItem.idCart!, newValueCart, true);
    };

    useEffect(() => {
        if (!isPaymentLink) return;
        setValue(`products.${index}.foreigner`, isForeignerInitialValue || false);
    }, [setValue, index, isForeignerInitialValue, isPaymentLink]);

    return (
        <CartSubItemContainer
            className={isInsideCombo ? 'combo-type' : 'normal-type'}
            shouldBlockEditing={shouldBlockEditing}
            shouldBlockItemChanges={shouldBlockItemChanges}
            isMissingSelection={isMissingSelection}
        >
            <div className="column-1">
                <div className="title-row">
                    <h3 className="title">{subItem.title}</h3>
                    {!isInsideCombo && (
                        <p className="price">
                            {Format.currency(subItem.price)}
                        </p>
                    )}
                </div>
                <div
                    className={
                        'date-row ' +
                        (isUndecidedDate(subItem.date) ? 'undecided' : '')
                    }
                    onClick={isItemEditingBlocked ? undefined : handleEditTime}
                >
                    <p>
                        <ReactIcon Icon={MdDateRange} />
                        {DateUtils.formatPossibleDate(subItem.date)}
                    </p>

                    {subItem.time && !isUndecidedDate(subItem.date) && (
                        <p>
                            <ReactIcon Icon={MdAccessTime} /> {subItem.time}
                        </p>
                    )}

                    {!isItemEditingBlocked && <MdEdit className="edit-icon" />}
                </div>
            </div>
            <div className="input-row">
                {isLimitedChoices ? (
                    <>
                        <SelectDependentsNameInput
                            dependents={dependents}
                            currentValue={findSubItem?.client?.name}
                            handleChangeCartValue={(value) => {
                                handleDependentDropdownChange(value);
                            }}
                            shouldBlockEditing={
                                shouldBlockEditing && !isPaymentLink
                            }
                        />
                        <input
                            type="text"
                            placeholder={isForeigner ? 'Document' : 'Documento'}
                            value={findSubItem?.client?.document}
                            maxLength={inputMasker.getMaxLength('CPF')}
                            onChange={({ target }) => {
                                handleChangeCartValue(target.value, 'document');
                            }}
                            readOnly={true}
                        />
                    </>
                ) : (
                    <>
                        <div className="field-div">
                            <input
                                type="text"
                                placeholder={
                                    isForeigner ? 'Full name' : 'Nome completo'
                                }
                                value={findSubItem?.client?.name}
                                {...register(`products.${index}.name`, {
                                    onChange: ({ target }) => {
                                        handleChangeCartValue(
                                            target.value,
                                            'name'
                                        );
                                    },
                                })}
                                className={
                                    error?.[index]?.name ? 'input-invalid' : ''
                                }
                                readOnly={shouldBlockEditing}
                            />
                            {(error?.[index]?.name ||
                                error?.[index]?.document) && (
                                <span className="error-span">
                                    {error?.[index]?.name?.message}
                                </span>
                            )}
                        </div>
                        <div className="field-div">
                            <div
                                style={{
                                    pointerEvents:
                                        shouldBlockEditing && !isPaymentLink
                                            ? 'none'
                                            : undefined,
                                }}
                            >
                                <Checkbox
                                    statusCheck={isForeigner}
                                    descriptionCheck={'Foreigner'}
                                    onClickCheck={handleChangeIsForeigner}
                                    variant={
                                        isForeigner ? 'secondary' : 'primary'
                                    }
                                />
                            </div>
                            <input
                                type="text"
                                placeholder={
                                    isForeigner ? 'Document' : 'Documento'
                                }
                                value={findSubItem?.client?.document}
                                maxLength={inputMasker.getMaxLength('CPF')}
                                {...register(`products.${index}.document`, {
                                    onChange: ({ target }) => {
                                        handleChangeCartValue(
                                            target.value,
                                            'document'
                                        );
                                    },
                                })}
                                className={
                                    error?.[index]?.document
                                        ? 'input-invalid'
                                        : ''
                                }
                                readOnly={shouldBlockEditing}
                            />
                            {(error?.[index]?.name ||
                                error?.[index]?.document) && (
                                <span className="error-span">
                                    {error?.[index]?.document?.message}
                                </span>
                            )}
                        </div>
                    </>
                )}
            </div>

            {!isInsideCombo && !shouldBlockItemChanges && (
                <IconButton className="actions" onClick={handleRemoveItem}>
                    <MdDelete />
                </IconButton>
            )}
        </CartSubItemContainer>
    );
};
